import { BaseModalProps } from '@/components/modals/base-modal/base-modal'
import FormModal from '@/components/modals/FormModal'
import { download } from '@/utils'
import CloseIcon from '@mui/icons-material/Close'
import DownloadIcon from '@mui/icons-material/Download'
import {
  Box,
  Button,
  CircularProgress,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material'
import { default as axios } from 'axios'
import { t } from 'i18next'
import React from 'react'
import { toast } from 'react-toastify'

interface FilePreviewModalProps extends BaseModalProps {
  id: string
  filename: string
  file: string
  extension?: string
}

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  isOpen,
  onClose,
  filename,
  extension,
  file,
}) => {
  const [isLoading, setIsLoading] = React.useState(true)
  const [fileUrl, setFileUrl] = React.useState<string>('')

  const isPDF = extension === 'pdf'
  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(filename)

  React.useEffect(() => {
    const fetchFile = async () => {
      setIsLoading(true)
      try {
        const response = await axios.get(file, {
          responseType: 'blob',
        })
        const url = URL.createObjectURL(response.data)
        setFileUrl(url)
      } catch (error) {
        toast.error(t('error_loading_file'))
      } finally {
        setIsLoading(false)
      }
    }

    if (isOpen && (isPDF || isImage)) {
      fetchFile()
    }

    if (!isPDF && !isImage) {
      setIsLoading(false)
    }

    return () => {
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl)
      }
    }
  }, [isOpen, file, isPDF, isImage])

  const handleDownload = async () => {
    try {
      const response = await axios.get(file, {
        responseType: 'blob',
      })
      download(response.data, filename)
    } catch (error) {
      toast.error(t('error_downloading_file'))
    }
  }

  const renderPreview = () => {
    if (isLoading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress />
        </Box>
      )
    }

    if (!isPDF && !isImage) {
      return (
        <Box textAlign="center" py={3}>
          <Typography color="textSecondary" variant="body1">
            {t('preview_not_available')}
          </Typography>
          <Typography color="textSecondary" variant="body2" sx={{ mt: 1 }}>
            {t('download_to_view')}
          </Typography>
        </Box>
      )
    }

    if (isPDF) {
      return (
        <iframe
          src={`${fileUrl}#toolbar=0`}
          style={{
            width: '100%',
            height: '70vh',
            border: 'none',
          }}
          title={filename}
        />
      )
    }

    if (isImage) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            maxHeight: '70vh',
            overflow: 'auto',
          }}
        >
          <img
            src={fileUrl}
            alt={filename}
            style={{
              maxWidth: '100%',
              maxHeight: '70vh',
              objectFit: 'contain',
            }}
          />
        </Box>
      )
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      actions={
        <>
          <Button onClick={onClose}>{t('close')}</Button>
          <Button startIcon={<DownloadIcon />} onClick={handleDownload}>
            {t('download')}
          </Button>
        </>
      }
    >
      <Box sx={{ position: 'relative' }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent>
          <Typography variant="h6" gutterBottom>
            {filename}
          </Typography>
          {renderPreview()}
        </DialogContent>
      </Box>
    </FormModal>
  )
}

export default FilePreviewModal
